
import { useState, useEffect, useCallback, useRef } from 'react';

interface User {
  id: string;
  email: string;
}

interface GameStats {
  id?: string;
  user_id?: string;
  total_games?: number;
  games_won?: number;
  games_lost?: number;
  total_earnings?: number;
  total_spent?: number;
  win_streak?: number;
  best_streak?: number;
  level?: number;
  experience?: number;
  achievements?: string[];
  created_at?: string;
  updated_at?: string;
}

interface GameArenaProps {
  user: User | null;
  gameStats: GameStats;
  onStatsUpdate: (newStats: Partial<GameStats>) => void;
  offlineMode?: boolean;
}

type GameMode = 'pattern' | 'memory' | 'speed' | 'color' | 'sequence' | 'reaction' | 'adventure' | 'shooter' | 'sports';

interface MemoryTile {
  id: number;
  isRevealed: boolean;
  isMatched: boolean;
  symbol: string;
}

export default function GameArena({ user, gameStats, onStatsUpdate, offlineMode = false }: GameArenaProps) {
  const [gameMode, setGameMode] = useState<GameMode>('pattern');
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);
  
  // Pattern Game States
  const [pattern, setPattern] = useState<number[]>([]);
  const [userPattern, setUserPattern] = useState<number[]>([]);
  const [showingPattern, setShowingPattern] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [patternLevel, setPatternLevel] = useState(1);
  
  // Memory Game States
  const [memoryTiles, setMemoryTiles] = useState<MemoryTile[]>([]);
  const [flippedTiles, setFlippedTiles] = useState<number[]>([]);
  const [memoryScore, setMemoryScore] = useState(0);
  
  // Speed Clicker States
  const [clicks, setClicks] = useState(0);
  const [targetClicks, setTargetClicks] = useState(50);
  
  // Color Rush States
  const [targetColor, setTargetColor] = useState('');
  const [colorOptions, setColorOptions] = useState<string[]>([]);
  const [colorScore, setColorScore] = useState(0);
  
  // Number Sequence States
  const [numberSequence, setNumberSequence] = useState<number[]>([]);
  const [currentNumber, setCurrentNumber] = useState(1);
  const [sequenceScore, setSequenceScore] = useState(0);
  
  // Reaction Time States
  const [reactionStarted, setReactionStarted] = useState(false);
  const [reactionTime, setReactionTime] = useState<number | null>(null);
  const [reactionStartTime, setReactionStartTime] = useState<number | null>(null);
  const [showReactionTarget, setShowReactionTarget] = useState(false);
  const [reactionRound, setReactionRound] = useState(0);
  
  // Adventure Game States
  const [adventureStage, setAdventureStage] = useState(1);
  const [adventureHealth, setAdventureHealth] = useState(100);
  const [adventureChoice, setAdventureChoice] = useState<string | null>(null);
  const [adventureStory, setAdventureStory] = useState('');
  const [adventureOptions, setAdventureOptions] = useState<{text: string, outcome: 'good' | 'bad' | 'neutral'}[]>([]);
  
  // Shooter Game States
  const [targets, setTargets] = useState<{id: number, x: number, y: number, type: 'enemy' | 'bonus' | 'danger', active: boolean}[]>([]);
  const [shooterScore, setShooterScore] = useState(0);
  const [ammo, setAmmo] = useState(30);
  const [shooterLevel, setShooterLevel] = useState(1);
  
  // Sports Game States
  const [sportsType, setSportsType] = useState<'basketball' | 'soccer' | 'racing'>('basketball');
  const [sportsScore, setSportsScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [ballPosition, setBallPosition] = useState(50);
  const [playerPower, setPlayerPower] = useState(0);
  const [isCharging, setIsCharging] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const reactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const patternTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const colors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'cyan'];
  const symbols = ['🎯', '⭐', '🔥', '💎', '🚀', '⚡', '🎪', '🎨', '🎭', '🎪', '🎯', '⭐'];

  // Timer effect
  useEffect(() => {
    if (isPlaying && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isPlaying) {
      endGame();
    }
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPlaying, timeLeft]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (reactionTimeoutRef.current) clearTimeout(reactionTimeoutRef.current);
      if (patternTimeoutRef.current) clearTimeout(patternTimeoutRef.current);
    };
  }, []);

  const startGame = () => {
    try {
      setGameError(null);
      setIsPlaying(true);
      setGameStarted(true);
      setScore(0);
      setTimeLeft(30);
      
      switch (gameMode) {
        case 'pattern':
          startPatternGame();
          break;
        case 'memory':
          startMemoryGame();
          break;
        case 'speed':
          startSpeedGame();
          break;
        case 'color':
          startColorGame();
          break;
        case 'sequence':
          startSequenceGame();
          break;
        case 'reaction':
          startReactionGame();
          break;
        case 'adventure':
          startAdventureGame();
          break;
        case 'shooter':
          startShooterGame();
          break;
        case 'sports':
          startSportsGame();
          break;
      }
    } catch (error) {
      console.error('Error starting game:', error);
      setGameError('Failed to start game. Please try again.');
      setIsPlaying(false);
      setGameStarted(false);
    }
  };

  const endGame = async () => {
    try {
      setIsPlaying(false);
      setGameStarted(false);
      let finalScore = score;
      
      switch (gameMode) {
        case 'memory':
          finalScore = memoryScore;
          break;
        case 'speed':
          finalScore = clicks;
          break;
        case 'color':
          finalScore = colorScore;
          break;
        case 'sequence':
          finalScore = sequenceScore;
          break;
        case 'reaction':
          finalScore = reactionTime ? Math.max(0, 1000 - reactionTime) : 0;
          break;
        case 'adventure':
          finalScore = adventureStage * 20 + adventureHealth;
          break;
        case 'shooter':
          finalScore = shooterScore;
          break;
        case 'sports':
          finalScore = sportsScore * 10;
          break;
      }
      
      // Calculate rewards based on score
      const baseReward = Math.floor(finalScore / 10) + 5; // Minimum 5 coins
      const bonusReward = finalScore > 100 ? Math.floor(finalScore / 20) : 0;
      const totalReward = baseReward + bonusReward;
      
      // Update game statistics
      const currentTotalGames = gameStats?.total_games || 0;
      const currentWins = gameStats?.games_won || 0;
      const currentEarnings = gameStats?.total_earnings || 0;
      const currentStreak = gameStats?.win_streak || 0;
      const currentBestStreak = gameStats?.best_streak || 0;
      
      const isWin = finalScore > 50; // Consider score > 50 as a win
      const newStreak = isWin ? currentStreak + 1 : 0;
      const newBestStreak = Math.max(currentBestStreak, newStreak);
      
      const updatedStats = {
        total_games: currentTotalGames + 1,
        games_won: isWin ? currentWins + 1 : currentWins,
        games_lost: !isWin ? (gameStats?.games_lost || 0) + 1 : (gameStats?.games_lost || 0),
        total_earnings: currentEarnings + totalReward,
        win_streak: newStreak,
        best_streak: newBestStreak,
        experience: (gameStats?.experience || 0) + Math.floor(finalScore / 5),
        level: Math.floor(((gameStats?.experience || 0) + Math.floor(finalScore / 5)) / 100) + 1
      };
      
      // Update stats with error handling
      try {
        await onStatsUpdate(updatedStats);
      } catch (error) {
        console.warn('Failed to update stats:', error);
        // Continue with game result display even if stats update fails
      }
      
      // Show game result
      showGameResult(finalScore, totalReward, isWin);
      
      resetGame();
    } catch (error) {
      console.error('Error ending game:', error);
      setGameError('Error saving game results. Progress may not be saved.');
      resetGame();
    }
  };

  const showGameResult = (finalScore: number, reward: number, isWin: boolean) => {
    try {
      const resultDiv = document.createElement('div');
      resultDiv.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50';
      resultDiv.innerHTML = `
        <div class="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-2xl p-8 text-center max-w-md mx-4">
          <div class="text-6xl mb-4">${isWin ? '🎉' : '🎮'}</div>
          <h3 class="text-2xl font-bold text-white mb-2">${isWin ? 'Great Job!' : 'Good Try!'}</h3>
          <p class="text-gray-300 mb-4">Final Score: <span class="text-cyan-400 font-bold">${finalScore}</span></p>
          <p class="text-green-400 font-bold text-xl mb-6">+${reward} PepCoins Earned!</p>
          ${offlineMode ? '<p class="text-yellow-400 text-sm mb-4">💾 Progress saved locally</p>' : ''}
          <button class="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300">
            Play Again
          </button>
        </div>
      `;
      
      document.body.appendChild(resultDiv);
      
      const playAgainBtn = resultDiv.querySelector('button');
      playAgainBtn?.addEventListener('click', () => {
        document.body.removeChild(resultDiv);
      });
      
      setTimeout(() => {
        if (document.body.contains(resultDiv)) {
          document.body.removeChild(resultDiv);
        }
      }, 5000);
    } catch (error) {
      console.error('Error showing game result:', error);
    }
  };

  const resetGame = () => {
    try {
      setPattern([]);
      setUserPattern([]);
      setCurrentStep(0);
      setShowingPattern(false);
      setPatternLevel(1);
      setMemoryTiles([]);
      setFlippedTiles([]);
      setMemoryScore(0);
      setClicks(0);
      setTargetClicks(50);
      setColorScore(0);
      setNumberSequence([]);
      setCurrentNumber(1);
      setSequenceScore(0);
      setReactionStarted(false);
      setReactionTime(null);
      setShowReactionTarget(false);
      setReactionRound(0);
      // Reset new game states
      setAdventureStage(1);
      setAdventureHealth(100);
      setAdventureChoice(null);
      setTargets([]);
      setShooterScore(0);
      setAmmo(30);
      setShooterLevel(1);
      setSportsScore(0);
      setOpponentScore(0);
      setBallPosition(50);
      setPlayerPower(0);
      setIsCharging(false);
      
      if (reactionTimeoutRef.current) clearTimeout(reactionTimeoutRef.current);
      if (patternTimeoutRef.current) clearTimeout(patternTimeoutRef.current);
    } catch (error) {
      console.error('Error resetting game:', error);
    }
  };

  // Pattern Game Logic
  const startPatternGame = () => {
    setPatternLevel(1);
    generateNewPattern(3); // Start with 3 tiles
  };

  const generateNewPattern = (length: number) => {
    const newPattern = Array.from({ length }, () => Math.floor(Math.random() * 9));
    setPattern(newPattern);
    setUserPattern([]);
    setCurrentStep(0);
    showPattern(newPattern);
  };

  const showPattern = (patternToShow: number[]) => {
    setShowingPattern(true);
    patternTimeoutRef.current = setTimeout(() => {
      setShowingPattern(false);
    }, Math.max(2000, patternToShow.length * 600));
  };

  const handlePatternClick = (index: number) => {
    if (showingPattern || !isPlaying) return;
    
    try {
      const newUserPattern = [...userPattern, index];
      setUserPattern(newUserPattern);
      
      if (newUserPattern[currentStep] === pattern[currentStep]) {
        if (newUserPattern.length === pattern.length) {
          // Pattern completed successfully
          setScore(score + (patternLevel * 10));
          setPatternLevel(patternLevel + 1);
          
          patternTimeoutRef.current = setTimeout(() => {
            generateNewPattern(Math.min(3 + patternLevel, 8)); // Increase difficulty
          }, 1000);
        } else {
          setCurrentStep(currentStep + 1);
        }
      } else {
        // Wrong tile clicked
        setScore(Math.max(0, score - 5));
        setUserPattern([]);
        setCurrentStep(0);
        showPattern(pattern);
      }
    } catch (error) {
      console.error('Error in pattern click:', error);
    }
  };

  // Memory Game Logic
  const startMemoryGame = () => {
    const shuffledSymbols = [...symbols, ...symbols].sort(() => Math.random() - 0.5);
    const tiles: MemoryTile[] = shuffledSymbols.slice(0, 16).map((symbol, index) => ({
      id: index,
      isRevealed: false,
      isMatched: false,
      symbol
    }));
    setMemoryTiles(tiles);
    setFlippedTiles([]);
    setMemoryScore(0);
  };

  const handleMemoryTileClick = (tileId: number) => {
    if (!isPlaying || flippedTiles.length >= 2) return;
    
    try {
      const tile = memoryTiles[tileId];
      if (tile.isRevealed || tile.isMatched) return;
      
      const newFlippedTiles = [...flippedTiles, tileId];
      setFlippedTiles(newFlippedTiles);
      
      const newTiles = memoryTiles.map(t => 
        t.id === tileId ? { ...t, isRevealed: true } : t
      );
      setMemoryTiles(newTiles);
      
      if (newFlippedTiles.length === 2) {
        const [first, second] = newFlippedTiles;
        if (memoryTiles[first].symbol === memoryTiles[second].symbol) {
          setTimeout(() => {
            setMemoryTiles(prev => prev.map(t => 
              t.id === first || t.id === second ? { ...t, isMatched: true } : t
            ));
            setMemoryScore(memoryScore + 10);
            setFlippedTiles([]);
          }, 500);
        } else {
          setTimeout(() => {
            setMemoryTiles(prev => prev.map(t => 
              t.id === first || t.id === second ? { ...t, isRevealed: false } : t
            ));
            setFlippedTiles([]);
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error in memory tile click:', error);
    }
  };

  // Speed Game Logic
  const startSpeedGame = () => {
    setClicks(0);
    setTargetClicks(50);
  };

  const handleSpeedClick = () => {
    if (!isPlaying) return;
    
    try {
      const newClicks = clicks + 1;
      setClicks(newClicks);
      
      if (newClicks >= targetClicks) {
        setScore(score + 20);
        setTargetClicks(targetClicks + 25);
      }
    } catch (error) {
      console.error('Error in speed click:', error);
    }
  };

  // Color Game Logic
  const startColorGame = () => {
    setColorScore(0);
    generateColorChallenge();
  };

  const generateColorChallenge = () => {
    try {
      const target = colors[Math.floor(Math.random() * colors.length)];
      const options = [target];
      while (options.length < 4) {
        const randomColor = colors[Math.floor(Math.random() * colors.length)];
        if (!options.includes(randomColor)) {
          options.push(randomColor);
        }
      }
      setTargetColor(target);
      setColorOptions(options.sort(() => Math.random() - 0.5));
    } catch (error) {
      console.error('Error generating color challenge:', error);
    }
  };

  const handleColorClick = (color: string) => {
    if (!isPlaying) return;
    
    try {
      if (color === targetColor) {
        setColorScore(colorScore + 5);
        generateColorChallenge();
      } else {
        setColorScore(Math.max(0, colorScore - 2));
      }
    } catch (error) {
      console.error('Error in color click:', error);
    }
  };

  // Sequence Game Logic
  const startSequenceGame = () => {
    try {
      const sequence = Array.from({ length: 16 }, (_, i) => i + 1).sort(() => Math.random() - 0.5);
      setNumberSequence(sequence);
      setCurrentNumber(1);
      setSequenceScore(0);
    } catch (error) {
      console.error('Error starting sequence game:', error);
    }
  };

  const handleNumberClick = (number: number) => {
    if (!isPlaying) return;
    
    try {
      if (number === currentNumber) {
        setCurrentNumber(currentNumber + 1);
        setSequenceScore(sequenceScore + 3);
        if (currentNumber === 16) {
          setScore(score + 50);
          startSequenceGame(); // Start new round
        }
      } else {
        setSequenceScore(Math.max(0, sequenceScore - 5));
      }
    } catch (error) {
      console.error('Error in number click:', error);
    }
  };

  // Reaction Game Logic
  const startReactionGame = () => {
    setReactionStarted(false);
    setReactionTime(null);
    setShowReactionTarget(false);
    setReactionRound(0);
    scheduleNextReaction();
  };

  const scheduleNextReaction = () => {
    try {
      const delay = Math.random() * 3000 + 1000; // 1-4 seconds
      reactionTimeoutRef.current = setTimeout(() => {
        setShowReactionTarget(true);
        setReactionStartTime(Date.now());
      }, delay);
    } catch (error) {
      console.error('Error scheduling reaction:', error);
    }
  };

  const handleReactionClick = () => {
    if (!showReactionTarget || !reactionStartTime || !isPlaying) return;
    
    try {
      const time = Date.now() - reactionStartTime;
      setReactionTime(time);
      setShowReactionTarget(false);
      
      // Score based on reaction time (faster = better)
      const reactionScore = Math.max(0, 200 - Math.floor(time / 5));
      setScore(score + reactionScore);
      setReactionRound(reactionRound + 1);
      
      setTimeout(() => {
        if (isPlaying) {
          scheduleNextReaction();
        }
      }, 1500);
    } catch (error) {
      console.error('Error in reaction click:', error);
    }
  };

  // Adventure Game Logic
  const adventureStories = [
    {
      story: "You enter a dark crypto dungeon. A mysterious chest glows ahead, but you hear footsteps behind you...",
      options: [
        { text: "🗡️ Draw your sword and face the threat", outcome: 'good' as const },
        { text: "📦 Rush to open the chest quickly", outcome: 'neutral' as const },
        { text: "🏃 Run deeper into the dungeon", outcome: 'bad' as const }
      ]
    },
    {
      story: "A dragon guards a pile of PepCoins! It notices you and prepares to attack...",
      options: [
        { text: "🛡️ Raise your shield and defend", outcome: 'good' as const },
        { text: "🔥 Cast a fire spell back", outcome: 'neutral' as const },
        { text: "💰 Try to grab coins and run", outcome: 'bad' as const }
      ]
    },
    {
      story: "You find a fork in the path. One leads to a bridge over lava, the other through a dark forest...",
      options: [
        { text: "🌉 Cross the lava bridge carefully", outcome: 'good' as const },
        { text: "🌲 Enter the mysterious forest", outcome: 'neutral' as const },
        { text: "⏪ Go back the way you came", outcome: 'bad' as const }
      ]
    },
    {
      story: "A wise wizard offers you a choice of three potions...",
      options: [
        { text: "💚 Green potion (Healing)", outcome: 'good' as const },
        { text: "💜 Purple potion (Mystery)", outcome: 'neutral' as const },
        { text: "🖤 Black potion (Power)", outcome: 'bad' as const }
      ]
    },
    {
      story: "You reach the treasure room! But it's guarded by a puzzle lock...",
      options: [
        { text: "🧩 Solve the puzzle carefully", outcome: 'good' as const },
        { text: "💪 Force the lock open", outcome: 'neutral' as const },
        { text: "🔑 Search for a hidden key", outcome: 'bad' as const }
      ]
    }
  ];

  const startAdventureGame = () => {
    setAdventureStage(1);
    setAdventureHealth(100);
    setTimeLeft(60);
    loadAdventureStory(0);
  };

  const loadAdventureStory = (index: number) => {
    const storyData = adventureStories[index % adventureStories.length];
    setAdventureStory(storyData.story);
    setAdventureOptions(storyData.options);
    setAdventureChoice(null);
  };

  const handleAdventureChoice = (outcome: 'good' | 'bad' | 'neutral') => {
    let healthChange = 0;
    let scoreChange = 0;
    
    if (outcome === 'good') {
      healthChange = 10;
      scoreChange = 30;
    } else if (outcome === 'neutral') {
      healthChange = -5;
      scoreChange = 15;
    } else {
      healthChange = -25;
      scoreChange = 5;
    }
    
    const newHealth = Math.max(0, Math.min(100, adventureHealth + healthChange));
    setAdventureHealth(newHealth);
    setScore(score + scoreChange);
    
    if (newHealth <= 0) {
      endGame();
      return;
    }
    
    setAdventureStage(adventureStage + 1);
    setTimeout(() => {
      loadAdventureStory(adventureStage);
    }, 1000);
  };

  // Shooter Game Logic
  const startShooterGame = () => {
    setShooterScore(0);
    setAmmo(30);
    setShooterLevel(1);
    setTimeLeft(45);
    spawnTargets();
  };

  const spawnTargets = () => {
    const newTargets = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 20,
      type: Math.random() > 0.7 ? 'bonus' : Math.random() > 0.8 ? 'danger' : 'enemy' as 'enemy' | 'bonus' | 'danger',
      active: true
    }));
    setTargets(newTargets);
  };

  const handleShoot = (targetId: number) => {
    if (ammo <= 0 || !isPlaying) return;
    
    setAmmo(ammo - 1);
    const target = targets.find(t => t.id === targetId);
    
    if (target && target.active) {
      let points = 0;
      if (target.type === 'enemy') {
        points = 10;
      } else if (target.type === 'bonus') {
        points = 25;
        setAmmo(prev => Math.min(prev + 5, 30));
      } else if (target.type === 'danger') {
        points = -15;
      }
      
      setShooterScore(shooterScore + points);
      setTargets(prev => prev.map(t => t.id === targetId ? { ...t, active: false } : t));
      
      // Respawn targets
      setTimeout(() => {
        if (isPlaying) {
          setTargets(prev => prev.map(t => 
            t.id === targetId 
              ? { ...t, x: Math.random() * 80 + 10, y: Math.random() * 60 + 20, active: true, type: Math.random() > 0.7 ? 'bonus' : Math.random() > 0.8 ? 'danger' : 'enemy' }
              : t
          ));
        }
      }, 500);
    }
  };

  // Sports Game Logic
  const startSportsGame = () => {
    setSportsScore(0);
    setOpponentScore(0);
    setBallPosition(50);
    setPlayerPower(0);
    setTimeLeft(30);
  };

  const handleSportsAction = (action: 'shoot' | 'pass' | 'defend') => {
    if (!isPlaying) return;
    
    const success = Math.random();
    
    if (action === 'shoot') {
      if (success > 0.4) {
        setSportsScore(sportsScore + (sportsType === 'basketball' ? 2 : 1));
        setBallPosition(50);
      } else {
        setOpponentScore(opponentScore + 1);
        setBallPosition(30);
      }
    } else if (action === 'pass') {
      if (success > 0.3) {
        setBallPosition(Math.min(100, ballPosition + 20));
      } else {
        setBallPosition(Math.max(0, ballPosition - 10));
      }
    } else if (action === 'defend') {
      if (success > 0.5) {
        setBallPosition(Math.min(100, ballPosition + 15));
      } else {
        setOpponentScore(opponentScore + 1);
      }
    }
  };

  const getGameModeTitle = () => {
    switch (gameMode) {
      case 'pattern': return 'Pattern Memory';
      case 'memory': return 'Memory Match';
      case 'speed': return 'Speed Clicker';
      case 'color': return 'Color Rush';
      case 'sequence': return 'Number Sequence';
      case 'reaction': return 'Reaction Time';
      case 'adventure': return 'Crypto Quest';
      case 'shooter': return 'Target Blaster';
      case 'sports': return 'Sports Arena';
      default: return 'Game Arena';
    }
  };

  const getCurrentScore = () => {
    switch (gameMode) {
      case 'memory': return memoryScore;
      case 'speed': return clicks;
      case 'color': return colorScore;
      case 'sequence': return sequenceScore;
      case 'reaction': return score;
      case 'pattern': return score;
      case 'adventure': return score;
      case 'shooter': return shooterScore;
      case 'sports': return sportsScore * 10;
      default: return score;
    }
  };

  const renderGameContent = () => {
    if (gameError) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-6">⚠️</div>
          <h3 className="text-2xl font-bold text-red-400 mb-4">Game Error</h3>
          <p className="text-gray-300 mb-8 max-w-md mx-auto">{gameError}</p>
          <button
            onClick={() => {
              setGameError(null);
              resetGame();
            }}
            className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2 mx-auto"
          >
            <i className="ri-refresh-line text-xl"></i>
            <span>Retry</span>
          </button>
        </div>
      );
    }

    if (!gameStarted) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-6">🎮</div>
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Play {getGameModeTitle()}?</h3>
          <p className="text-gray-300 mb-8 max-w-md mx-auto">
            {gameMode === 'pattern' && 'Memorize and repeat increasingly complex patterns to earn points!'}
            {gameMode === 'memory' && 'Find matching pairs of symbols as quickly as possible!'}
            {gameMode === 'speed' && 'Click as fast as you can to reach the target!'}
            {gameMode === 'color' && 'Quickly identify and click the correct colors!'}
            {gameMode === 'sequence' && 'Click numbers in ascending order from 1 to 16!'}
            {gameMode === 'reaction' && 'Test your reflexes by clicking when the target appears!'}
            {gameMode === 'adventure' && 'Embark on an epic quest! Make choices to survive and collect treasure!'}
            {gameMode === 'shooter' && 'Shoot targets to score points! Avoid danger zones and collect bonuses!'}
            {gameMode === 'sports' && 'Compete in fast-paced sports action! Score goals and defend your territory!'}
          </p>
          {offlineMode && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <div className="flex items-center gap-2 text-yellow-300">
                <i className="ri-wifi-off-line"></i>
                <span className="text-sm">Playing in offline mode - progress saved locally</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    switch (gameMode) {
      case 'pattern':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-300 mb-2">
                {showingPattern ? 'Memorize the pattern!' : 'Repeat the pattern by clicking the tiles'}
              </p>
              <p className="text-sm text-cyan-400 mb-4">Level {patternLevel} - Pattern Length: {pattern.length}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {Array.from({ length: 9 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePatternClick(index)}
                  disabled={showingPattern}
                  className={`w-20 h-20 rounded-xl font-bold text-white transition-all duration-300 transform hover:scale-105 ${
                    showingPattern && pattern.includes(index)
                      ? 'bg-cyan-500 scale-110 shadow-lg shadow-cyan-500/50'
                      : userPattern.includes(index)
                      ? 'bg-purple-500 shadow-lg shadow-purple-500/50'
                      : 'bg-gray-700 hover:bg-gray-600'
                  } ${showingPattern ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        );

      case 'memory':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-300 mb-4">Match the pairs! Score: {memoryScore}</p>
            </div>
            <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
              {memoryTiles.map((tile) => (
                <button
                  key={tile.id}
                  onClick={() => handleMemoryTileClick(tile.id)}
                  className={`w-16 h-16 rounded-xl font-bold text-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                    tile.isRevealed || tile.isMatched
                      ? tile.isMatched
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/50'
                        : 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/50'
                      : 'bg-gray-700 hover:bg-gray-600 text-transparent'
                  }`}
                >
                  {tile.isRevealed || tile.isMatched ? tile.symbol : '?'}
                </button>
              ))}
            </div>
          </div>
        );

      case 'speed':
        return (
          <div className="space-y-6 text-center">
            <div>
              <p className="text-gray-300 mb-4">Click as fast as you can!</p>
              <p className="text-2xl font-bold text-cyan-400 mb-2">{clicks} / {targetClicks}</p>
              <div className="w-full bg-gray-700 rounded-full h-3 mb-4 max-w-md mx-auto">
                <div 
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((clicks / targetClicks) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            <button
              onClick={handleSpeedClick}
              className="w-32 h-32 bg-gradient-to-r from-red-500 to-pink-600 rounded-full text-white font-bold text-xl hover:scale-110 transition-all duration-200 active:scale-95 cursor-pointer shadow-lg hover:shadow-red-500/50"
            >
              CLICK!
            </button>
          </div>
        );

      case 'color':
        return (
          <div className="space-y-6 text-center">
            <div>
              <p className="text-gray-300 mb-4">Click the <span className="font-bold text-cyan-400 capitalize">{targetColor}</span> color!</p>
              <p className="text-xl font-bold text-white">Score: {colorScore}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
              {colorOptions.map((color, index) => (
                <button
                  key={index}
                  onClick={() => handleColorClick(color)}
                  className={`w-24 h-24 rounded-xl font-bold text-white transition-all duration-300 hover:scale-110 cursor-pointer shadow-lg ${
                    color === 'red' ? 'bg-red-500 hover:shadow-red-500/50' :
                    color === 'blue' ? 'bg-blue-500 hover:shadow-blue-500/50' :
                    color === 'green' ? 'bg-green-500 hover:shadow-green-500/50' :
                    color === 'yellow' ? 'bg-yellow-500 hover:shadow-yellow-500/50' :
                    color === 'purple' ? 'bg-purple-500 hover:shadow-purple-500/50' :
                    color === 'orange' ? 'bg-orange-500 hover:shadow-orange-500/50' :
                    color === 'pink' ? 'bg-pink-500 hover:shadow-pink-500/50' :
                    'bg-cyan-500 hover:shadow-cyan-500/50'
                  }`}
                >
                  {color.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        );

      case 'sequence':
        return (
          <div className="space-y-6 text-center">
            <div>
              <p className="text-gray-300 mb-4">Click numbers in order: 1, 2, 3...</p>
              <p className="text-xl font-bold text-white">Next: {currentNumber} | Score: {sequenceScore}</p>
            </div>
            <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
              {numberSequence.map((number, index) => (
                <button
                  key={index}
                  onClick={() => handleNumberClick(number)}
                  className={`w-12 h-12 rounded-xl font-bold text-white transition-all duration-300 cursor-pointer ${
                    number < currentNumber
                      ? 'bg-green-500 shadow-lg shadow-green-500/50'
                      : number === currentNumber
                      ? 'bg-cyan-500 hover:scale-110 shadow-lg shadow-cyan-500/50 animate-pulse'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  {number}
                </button>
              ))}
            </div>
          </div>
        );

      case 'reaction':
        return (
          <div className="space-y-6 text-center">
            <div>
              <p className="text-gray-300 mb-4">
                {!showReactionTarget ? 'Wait for the target...' : 'CLICK NOW!'}
              </p>
              <p className="text-sm text-gray-400 mb-2">Round: {reactionRound + 1}</p>
              {reactionTime && (
                <p className="text-xl font-bold text-cyan-400">{reactionTime}ms</p>
              )}
            </div>
            <div className="flex justify-center">
              {showReactionTarget ? (
                <button
                  onClick={handleReactionClick}
                  className="w-32 h-32 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full text-white font-bold text-xl animate-pulse hover:scale-110 transition-all duration-200 cursor-pointer shadow-lg shadow-green-500/50"
                >
                  CLICK!
                </button>
              ) : (
                <div className="w-32 h-32 bg-gray-700 rounded-full flex items-center justify-center">
                  <span className="text-gray-400">Wait...</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'adventure':
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="flex justify-center gap-6 mb-4">
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-2">
                  <span className="text-red-400 font-bold">❤️ {adventureHealth}</span>
                </div>
                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg px-4 py-2">
                  <span className="text-purple-400 font-bold">📍 Stage {adventureStage}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-indigo-500/30 rounded-2xl p-6 max-w-lg mx-auto">
              <p className="text-lg text-gray-200 mb-6 leading-relaxed">{adventureStory}</p>
              
              <div className="space-y-3">
                {adventureOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAdventureChoice(option.outcome)}
                    className="w-full bg-gray-800/80 hover:bg-gray-700/80 border border-gray-600 hover:border-purple-500/50 rounded-xl p-4 text-left transition-all duration-300 transform hover:scale-102 cursor-pointer"
                  >
                    <span className="text-white">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'shooter':
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="flex justify-center gap-4 mb-4">
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg px-4 py-2">
                  <span className="text-yellow-400 font-bold">🎯 {shooterScore}</span>
                </div>
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg px-4 py-2">
                  <span className="text-blue-400 font-bold">🔫 {ammo}</span>
                </div>
              </div>
              <p className="text-sm text-gray-400">🟢 Enemy (+10) | 🟡 Bonus (+25 & ammo) | 🔴 Danger (-15)</p>
            </div>
            
            <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-2xl h-80 overflow-hidden">
              {targets.map((target) => (
                target.active && (
                  <button
                    key={target.id}
                    onClick={() => handleShoot(target.id)}
                    className={`absolute w-12 h-12 rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 hover:scale-125 cursor-crosshair animate-pulse ${
                      target.type === 'enemy' ? 'bg-green-500 shadow-lg shadow-green-500/50' :
                      target.type === 'bonus' ? 'bg-yellow-500 shadow-lg shadow-yellow-500/50' :
                      'bg-red-500 shadow-lg shadow-red-500/50'
                    }`}
                    style={{ left: `${target.x}%`, top: `${target.y}%` }}
                  >
                    {target.type === 'enemy' ? '👾' : target.type === 'bonus' ? '⭐' : '💀'}
                  </button>
                )
              ))}
            </div>
          </div>
        );

      case 'sports':
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="flex justify-center gap-8 mb-4">
                <div className="text-center">
                  <div className="text-3xl font-black text-green-400">{sportsScore}</div>
                  <div className="text-sm text-gray-400">You</div>
                </div>
                <div className="text-2xl text-gray-500">VS</div>
                <div className="text-center">
                  <div className="text-3xl font-black text-red-400">{opponentScore}</div>
                  <div className="text-sm text-gray-400">CPU</div>
                </div>
              </div>
              
              {/* Ball Position Indicator */}
              <div className="w-full bg-gray-700 rounded-full h-4 mb-4 relative">
                <div 
                  className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-white rounded-full shadow-lg transition-all duration-300"
                  style={{ left: `${ballPosition}%` }}
                >
                  ⚽
                </div>
              </div>
              <p className="text-sm text-gray-400">Ball Position: {ballPosition}%</p>
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleSportsAction('defend')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-4 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                🛡️ Defend
              </button>
              <button
                onClick={() => handleSportsAction('pass')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-4 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                ⚡ Pass
              </button>
              <button
                onClick={() => handleSportsAction('shoot')}
                disabled={ballPosition < 60}
                className={`px-6 py-4 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 ${
                  ballPosition >= 60 
                    ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer' 
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                🥅 Shoot
              </button>
            </div>
            
            {ballPosition < 60 && (
              <p className="text-center text-sm text-yellow-400">Pass to move the ball forward before shooting!</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 border border-gray-800">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">{getGameModeTitle()}</h2>
        
        {/* Game Mode Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {[
            { mode: 'pattern' as GameMode, label: 'Pattern', icon: 'ri-grid-line' },
            { mode: 'memory' as GameMode, label: 'Memory', icon: 'ri-brain-line' },
            { mode: 'speed' as GameMode, label: 'Speed', icon: 'ri-flashlight-line' },
            { mode: 'color' as GameMode, label: 'Color', icon: 'ri-palette-line' },
            { mode: 'sequence' as GameMode, label: 'Sequence', icon: 'ri-sort-asc' },
            { mode: 'reaction' as GameMode, label: 'Reaction', icon: 'ri-timer-line' },
            { mode: 'adventure' as GameMode, label: 'Quest', icon: 'ri-sword-line' },
            { mode: 'shooter' as GameMode, label: 'Shooter', icon: 'ri-crosshair-2-line' },
            { mode: 'sports' as GameMode, label: 'Sports', icon: 'ri-football-line' }
          ].map(({ mode, label, icon }) => (
            <button
              key={mode}
              onClick={() => {
                if (!isPlaying) {
                  setGameMode(mode);
                  resetGame();
                  setGameError(null);
                }
              }}
              disabled={isPlaying}
              className={`px-4 py-2 rounded-xl font-bold transition-all duration-300 ${
                gameMode === mode
                  ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${isPlaying ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <i className={`${icon} mr-2`}></i>
              {label}
            </button>
          ))}
        </div>

        {/* Game Stats */}
        <div className="flex justify-center gap-8 mb-6">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Score</p>
            <p className="text-2xl font-bold text-cyan-400">
              {getCurrentScore()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-sm">Time</p>
            <p className="text-2xl font-bold text-white">{timeLeft}s</p>
          </div>
          {gameStarted && (
            <div className="text-center">
              <p className="text-gray-400 text-sm">Level</p>
              <p className="text-2xl font-bold text-purple-400">
                {gameMode === 'pattern' ? patternLevel : gameMode === 'adventure' ? adventureStage : gameMode === 'shooter' ? shooterLevel : 1}
              </p>
            </div>
          )}
        </div>

        {/* Start/Stop Button */}
        {!gameStarted ? (
          <button
            onClick={startGame}
            disabled={!!gameError}
            className={`bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-bold transition-all duration-300 whitespace-nowrap transform hover:scale-105 shadow-lg hover:shadow-cyan-500/25 ${
              gameError ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <i className="ri-play-line mr-2"></i>
            Start Game
          </button>
        ) : (
          <button
            onClick={endGame}
            className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-lg hover:shadow-red-500/25"
          >
            <i className="ri-stop-line mr-2"></i>
            End Game
          </button>
        )}
      </div>

      {/* Game Content */}
      <div className="min-h-[400px] flex items-center justify-center">
        {renderGameContent()}
      </div>
    </div>
  );
}
